import type { TFunction } from 'i18next'

import { DISCOUNT_DAYS, type DiscountDay } from './page-discounts'

/**
 * A page location's opening hours, as the server stores them: one entry per
 * weekday ("mon".."sun", a missing day = no information) plus dated
 * exceptions that win over the weekday on their date. Times are "HH:mm"
 * local to the place; a close at or before the open means it stays open
 * past midnight. "Today" is the device's date.
 */

export const HOURS_DAYS = DISCOUNT_DAYS
export type HoursDay = DiscountDay

export interface PageHoursSlot {
  closed: boolean
  /** "HH:mm"; set when open. */
  open?: string | null
  close?: string | null
}

export interface PageHoursDate extends PageHoursSlot {
  /** "yyyy-MM-dd". */
  date: string
}

export interface PageHours {
  week: Partial<Record<HoursDay, PageHoursSlot>>
  dates: PageHoursDate[]
}

/** The default times of a row switched to Open. */
export const DEFAULT_OPEN = '09:00'
export const DEFAULT_CLOSE = '18:00'

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function emptyHours(): PageHours {
  return { week: {}, dates: [] }
}

function readSlot(raw: unknown): PageHoursSlot | null {
  if (!raw || typeof raw !== 'object') return null
  const slot = raw as Record<string, unknown>
  if (slot.closed === true) return { closed: true }
  const open = typeof slot.open === 'string' ? slot.open : null
  const close = typeof slot.close === 'string' ? slot.close : null
  if (!open || !close || !TIME_PATTERN.test(open) || !TIME_PATTERN.test(close)) return null
  return { closed: false, open, close }
}

/** The timetable from a payload, leniently: anything malformed is dropped; null = none. */
export function readHours(raw: unknown): PageHours | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as { week?: unknown; dates?: unknown }
  const hours = emptyHours()
  if (source.week && typeof source.week === 'object') {
    for (const day of HOURS_DAYS) {
      const slot = readSlot((source.week as Record<string, unknown>)[day])
      if (slot) hours.week[day] = slot
    }
  }
  if (Array.isArray(source.dates)) {
    for (const item of source.dates) {
      const slot = readSlot(item)
      const date = (item as { date?: unknown } | null)?.date
      if (slot && typeof date === 'string' && DATE_PATTERN.test(date)) {
        hours.dates.push({ ...slot, date })
      }
    }
  }
  return hours
}

export function hasHours(hours: PageHours | null | undefined): hours is PageHours {
  return hours != null && (Object.keys(hours.week).length > 0 || hours.dates.length > 0)
}

/** The timetable as it travels: incomplete rows (a date not picked yet) left out, dates sorted. */
export function cleanHours(hours: PageHours): PageHours {
  const week: PageHours['week'] = {}
  for (const day of HOURS_DAYS) {
    const slot = hours.week[day]
    if (!slot) continue
    week[day] = slot.closed
      ? { closed: true }
      : { closed: false, open: slot.open ?? DEFAULT_OPEN, close: slot.close ?? DEFAULT_CLOSE }
  }
  const dates = hours.dates
    .filter((item) => DATE_PATTERN.test(item.date))
    .map((item) =>
      item.closed
        ? { date: item.date, closed: true }
        : {
            date: item.date,
            closed: false,
            open: item.open ?? DEFAULT_OPEN,
            close: item.close ?? DEFAULT_CLOSE,
          },
    )
    .sort((a, b) => a.date.localeCompare(b.date))
  return { week, dates }
}

/** Same timetable, whatever the key order. */
export function sameHours(a: PageHours | null | undefined, b: PageHours | null | undefined): boolean {
  const canonical = (hours: PageHours | null | undefined) =>
    JSON.stringify(hasHours(hours) ? cleanHours(hours) : emptyHours())
  return canonical(a) === canonical(b)
}

/** "yyyy-MM-dd" of a date on the device's calendar. */
export function localDateKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** The weekday key of a date on the device's calendar. */
export function dayKeyOf(date: Date): HoursDay {
  return HOURS_DAYS[(date.getDay() + 6) % 7]
}

/** A day's entry: its dated exception first, else its weekday; null = no information. */
export function slotOn(hours: PageHours | null | undefined, date: Date): PageHoursSlot | null {
  if (!hours) return null
  const key = localDateKey(date)
  return hours.dates.find((item) => item.date === key) ?? hours.week[dayKeyOf(date)] ?? null
}

function minutesOf(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * Open right now by the device clock, counting yesterday's past-midnight
 * hours; null when neither day says anything.
 */
export function isOpenNow(hours: PageHours | null | undefined, now = new Date()): boolean | null {
  if (!hasHours(hours)) return null
  const minutes = now.getHours() * 60 + now.getMinutes()
  const today = slotOn(hours, now)
  const yesterday = slotOn(hours, new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))
  // Yesterday's hours running past midnight still cover the early morning.
  if (yesterday && !yesterday.closed && yesterday.open && yesterday.close) {
    const open = minutesOf(yesterday.open)
    const close = minutesOf(yesterday.close)
    if (close <= open && minutes < close) return true
  }
  if (!today) return yesterday ? false : null
  if (today.closed || !today.open || !today.close) return false
  const open = minutesOf(today.open)
  const close = minutesOf(today.close)
  return close > open ? minutes >= open && minutes < close : minutes >= open
}

/** "HH:mm" in the device's time format. */
export function formatTime(time: string, language: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  try {
    return new Intl.DateTimeFormat(language, { hour: 'numeric', minute: '2-digit' }).format(
      new Date(2024, 0, 1, hours, minutes),
    )
  } catch {
    return time
  }
}

/** "09:00 - 18:00" for an open entry; null for a closed one. */
export function formatSlot(slot: PageHoursSlot, language: string): string | null {
  if (slot.closed || !slot.open || !slot.close) return null
  return `${formatTime(slot.open, language)} - ${formatTime(slot.close, language)}`
}

/** The short weekday name ("Mon") of a key in the given language. */
export function dayName(day: HoursDay, language: string): string {
  // 2024-01-01 is a Monday.
  const date = new Date(2024, 0, 1 + HOURS_DAYS.indexOf(day))
  try {
    return new Intl.DateTimeFormat(language, { weekday: 'short' }).format(date)
  } catch {
    return day
  }
}

/** A "yyyy-MM-dd" date in the device's short format. */
export function formatDateKey(key: string, language: string): string {
  const [year, month, day] = key.split('-').map(Number)
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(
      new Date(year, month - 1, day),
    )
  } catch {
    return key
  }
}

/** The dated exceptions from today on, in date order. */
export function upcomingDates(hours: PageHours | null | undefined, now = new Date()): PageHoursDate[] {
  if (!hours) return []
  const today = localDateKey(now)
  return hours.dates
    .filter((item) => item.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Today's line of a timetable: "Today: 09:00 - 18:00", "Closed today", or
 * "Hours not set" when the timetable says nothing about today.
 */
export function todayHoursText(
  hours: PageHours | null | undefined,
  t: TFunction<'profile'>,
  language: string,
): string {
  const today = hasHours(hours) ? slotOn(hours, new Date()) : null
  if (!today) return t('pages.hours.notSet')
  const range = formatSlot(today, language)
  return range ? t('pages.hours.today', { hours: range }) : t('pages.hours.todayClosed')
}

/** Today's entry says closed (the "Closed today" line, drawn red). */
export function isClosedToday(hours: PageHours | null | undefined, now = new Date()): boolean {
  return hasHours(hours) && slotOn(hours, now)?.closed === true
}
